/**
 * Nuxt 3 호환 레이어
 *
 * 기존 unsedang-cli(Nuxt) 코드를 수정 없이 Vue + Vite 환경에서 돌리기 위한 shim.
 * 페이지/컴포저블에서 쓰이던 Nuxt 전역 API를 동일한 시그니처로 제공한다.
 */
import { ref, reactive, shallowRef, shallowReactive, watch, getCurrentInstance } from "vue";
import { useRegisterSW } from "virtual:pwa-register/vue";

export { useHead, useSeoMeta } from "@unhead/vue";

// `import { ... } from "#app"` / "#imports" 로 가져오던 것들을 그대로 재export
// NOTE: useRoute / useRouter 는 vue-router 것을 쓰지 않는다. vue-router 의 구현은
//       inject() 기반이라 컴포넌트 setup() 밖(플러그인·미들웨어)에서는 undefined 가 된다.
//       Nuxt 는 nuxtApp 에서 꺼내므로 어디서든 동작한다 — 아래에서 같은 방식으로 제공한다.
export { useLink } from "vue-router";
export {
  ref,
  shallowRef,
  computed,
  reactive,
  readonly,
  watch,
  watchEffect,
  nextTick,
  toRef,
  toRefs,
  unref,
  isRef,
  provide,
  inject,
  onMounted,
  onBeforeMount,
  onUnmounted,
  onBeforeUnmount,
  onActivated,
  onDeactivated,
  defineComponent,
} from "vue";

/* ------------------------------------------------------------------ */
/* nuxtApp / router 주입                                               */
/* ------------------------------------------------------------------ */

let _router = null;
let _nuxtApp = null;

const _hooks = new Map();

// @vite-pwa/nuxt 의 useNuxtApp().$pwa 와 동일한 인터페이스({ needRefresh, offlineReady, updateServiceWorker }).
// registerType: "prompt" 이므로 새 서비스워커가 감지돼도 자동 새로고침 없이 needRefresh 만 true 로 바뀐다.
// (registerSW 는 앱 전체에서 한 번만 호출되어야 하므로 모듈 스코프에서 한 번만 생성한다.)
const _pwa = reactive(useRegisterSW());

export function createNuxtApp({ vueApp, router, pinia }) {
  _router = router;

  // Nuxt 의 nuxtApp._route 와 동일한 구조.
  // router.currentRoute 는 네비게이션마다 **새 객체**로 바뀌므로, 컴포저블이
  // `const route = useRoute()` 로 들고 있으면 갱신을 놓친다.
  // 그래서 참조가 고정된 객체에 getter 를 심어 shallowRef 를 통해 읽게 한다.
  const _routeRef = shallowRef(router.currentRoute.value);
  router.afterEach(() => {
    _routeRef.value = router.currentRoute.value;
  });

  const route = {};
  for (const key in _routeRef.value) {
    Object.defineProperty(route, key, {
      get: () => _routeRef.value[key],
      enumerable: true,
    });
  }

  _nuxtApp = {
    vueApp,
    _route: shallowReactive(route),
    $router: router,
    $pinia: pinia,
    $pwa: _pwa,
    hook(name, cb) {
      if (!_hooks.has(name)) _hooks.set(name, []);
      _hooks.get(name).push(cb);
    },
    async callHook(name, ...args) {
      for (const cb of _hooks.get(name) || []) await cb(...args);
    },
  };
  return _nuxtApp;
}

export const useNuxtApp = () => _nuxtApp;

// Nuxt: useRouter() = useNuxtApp()?.$router / useRoute() = useNuxtApp()._route
// setup() 안이든 밖이든 동일하게 동작한다.
export const useRouter = () => _router;
export const useRoute = () => _nuxtApp?._route ?? _router?.currentRoute.value;

/* ------------------------------------------------------------------ */
/* useRuntimeConfig                                                    */
/* ------------------------------------------------------------------ */

// nuxt.config.ts 의 runtimeConfig.public 과 동일한 형태.
// 값은 vite.config.js 가 .env.* 파일을 직접 읽어 __NUXT_RUNTIME_CONFIG__ 로 주입한다.
// (Nuxt 의 `--dotenv .env.dev` 와 같은 파일을 그대로 쓰기 위함)
const runtimeConfig = {
  public: { ...__NUXT_RUNTIME_CONFIG__ },
};

// dev / preview 서버에서는 vite 의 server.proxy 가 /api 를 백엔드로 넘겨준다.
// 이때 apiServer 를 현재 오리진으로 바꿔 `${apiServer}/api/...` 가 같은 오리진 요청이 되게 한다.
// (운영에서 VUE_API_SERVER 가 nginx 도메인 = 사이트 오리진인 것과 동일한 상태)
if (__DEV_API_PROXY__) {
  runtimeConfig.public.apiServer = window.location.origin;
}

export const useRuntimeConfig = () => runtimeConfig;
export const useAppConfig = () => ({});

/* ------------------------------------------------------------------ */
/* useState — 키 기반 전역 공유 ref (Nuxt와 동일하게 값이 undefined일 때만 초기화) */
/* ------------------------------------------------------------------ */

const _states = new Map();

export function useState(key, init) {
  if (typeof key !== "string") {
    init = key;
    key = "$auto:" + _states.size;
  }
  if (!_states.has(key)) _states.set(key, ref(undefined));

  const state = _states.get(key);
  if (state.value === undefined && init !== undefined) {
    state.value = typeof init === "function" ? init() : init;
  }
  return state;
}

export function clearNuxtState(keys) {
  if (!keys) return _states.clear();
  for (const k of Array.isArray(keys) ? keys : [keys]) _states.delete(k);
}

/* ------------------------------------------------------------------ */
/* useCookie — document.cookie 와 동기화되는 ref                        */
/* ------------------------------------------------------------------ */

// Nuxt(destr)와 동일하게 JSON 형태면 파싱해서 돌려준다.
function decodeCookieValue(raw) {
  if (raw === undefined || raw === null) return null;
  const v = raw.trim();
  if (v === "") return null;
  if (v === "true") return true;
  if (v === "false") return false;
  if (v === "null") return null;
  if (/^[[{"]/.test(v) || /^-?\d+(\.\d+)?$/.test(v)) {
    try {
      return JSON.parse(v);
    } catch {
      return raw;
    }
  }
  return raw;
}

function readRawCookie(name) {
  const target = encodeURIComponent(name) + "=";
  for (const part of document.cookie.split(";")) {
    const c = part.trim();
    if (c.startsWith(target)) return decodeURIComponent(c.slice(target.length));
  }
  return undefined;
}

function writeCookie(name, value, opts = {}) {
  const key = encodeURIComponent(name);

  if (value === null || value === undefined) {
    document.cookie = `${key}=; Max-Age=0; path=${opts.path || "/"}`;
    return;
  }

  const raw = typeof value === "string" ? value : JSON.stringify(value);
  let cookie = `${key}=${encodeURIComponent(raw)}`;

  cookie += `; path=${opts.path || "/"}`;
  if (opts.maxAge !== undefined) cookie += `; Max-Age=${opts.maxAge}`;
  if (opts.expires) cookie += `; Expires=${new Date(opts.expires).toUTCString()}`;
  if (opts.domain) cookie += `; Domain=${opts.domain}`;
  if (opts.sameSite) cookie += `; SameSite=${opts.sameSite}`;
  if (opts.secure) cookie += "; Secure";

  document.cookie = cookie;
}

const _cookies = new Map();

/**
 * Nuxt 는 useCookie() 호출마다 새 ref 를 만들지만, 여기서는 이름당 하나의 ref 를 공유한다.
 * 그래서 두 가지를 지켜야 한다.
 *
 *  1) 쓰기는 flush: "sync" — 값을 넣는 즉시 document.cookie 에 반영되어야 한다.
 *     (useAuthState.setAuth 는 쿠키를 세팅한 직후 같은 tick 안에서 useMyLikes() →
 *      useApi() → useAuthState() → useCookie("auth") 를 다시 호출한다.
 *      비동기 flush 였다면 그 시점에 document.cookie 가 비어 있어 아래 재동기화가
 *      방금 넣은 값을 지워버린다.)
 *
 *  2) 재동기화는 raw 쿠키 문자열이 **실제로 바뀌었을 때만** 한다.
 *     브라우저가 쿠키 저장을 거부한 경우(HTTP + Secure 등) 메모리 값까지
 *     날려버리면 안 되기 때문이다.
 */
export function useCookie(name, opts = {}) {
  if (!_cookies.has(name)) {
    const raw = readRawCookie(name);
    const entry = { state: ref(decodeCookieValue(raw)), opts, lastRaw: raw };
    _cookies.set(name, entry);

    watch(
      entry.state,
      (val) => {
        writeCookie(name, val, entry.opts);
        entry.lastRaw = readRawCookie(name);
      },
      { deep: true, flush: "sync" },
    );
  }

  const entry = _cookies.get(name);
  entry.opts = { ...entry.opts, ...opts };

  // 외부(다른 탭, 서버 Set-Cookie 등)에서 바뀐 값만 반영
  const raw = readRawCookie(name);
  if (raw !== entry.lastRaw) {
    entry.lastRaw = raw;
    entry.state.value = decodeCookieValue(raw);
  }

  return entry.state;
}

export function refreshCookie(name) {
  const entry = _cookies.get(name);
  if (!entry) return;
  entry.lastRaw = readRawCookie(name);
  entry.state.value = decodeCookieValue(entry.lastRaw);
}

/* ------------------------------------------------------------------ */
/* navigateTo / 미들웨어                                                */
/* ------------------------------------------------------------------ */

let _inMiddleware = false;

function toLocation(to) {
  const resolved = _router.resolve(to);
  return { path: resolved.path, query: resolved.query, hash: resolved.hash };
}

export function navigateTo(to, options = {}) {
  if (!to) to = "/";

  const isExternal =
    options.external === true ||
    (typeof to === "string" && /^(https?:)?\/\/|^mailto:|^tel:/.test(to));

  if (isExternal) {
    const href = typeof to === "string" ? to : _router.resolve(to).href;
    if (options.open) window.open(href, options.open.target || "_blank");
    else if (options.replace) window.location.replace(href);
    else window.location.href = href;
    return;
  }

  // 라우트 미들웨어 안에서는 네비게이션을 직접 실행하지 않고
  // vue-router 가드가 처리하도록 목적지를 반환한다.
  if (_inMiddleware) {
    const loc = toLocation(to);
    if (options.replace) loc.replace = true;
    return loc;
  }

  return options.replace ? _router.replace(to) : _router.push(to);
}

export const defineNuxtRouteMiddleware = (fn) => fn;

/** Nuxt의 global middleware를 vue-router 가드로 변환 */
export function createMiddlewareGuard(middleware) {
  return async (to, from) => {
    _inMiddleware = true;
    try {
      return await middleware(to, from);
    } finally {
      _inMiddleware = false;
    }
  };
}

export const abortNavigation = (err) => {
  if (err) console.error("abortNavigation:", err);
  return false;
};

/* ------------------------------------------------------------------ */
/* definePageMeta — 라우트 가드 (Nuxt 와 같은 시점에 실행)               */
/* ------------------------------------------------------------------ */

// vite.config.js 의 nuxt-page-meta-macro 가 페이지의 definePageMeta() 를
// `export const __pageMeta` 로 끌어올려 둔다. 그 값을 여기서 읽어 실행한다.
const _pageMetaCache = new WeakMap();

async function resolvePageMeta(record) {
  if (_pageMetaCache.has(record)) return _pageMetaCache.get(record);

  const comp = record.components?.default;

  // unplugin-vue-router 는 컴포넌트를 항상 `() => import(...)` 로 넣는다.
  // vue-router 는 로드 후 이 자리를 실제 컴포넌트 객체로 바꿔치기 하므로,
  // 로더가 보이는 건 첫 방문뿐이다 → record 단위로 결과를 캐시한다.
  // (같은 import() 라 모듈 캐시를 타므로 추가 네트워크 요청은 없다)
  if (typeof comp !== "function") {
    _pageMetaCache.set(record, null);
    return null;
  }

  let meta = null;
  try {
    meta = (await comp())?.__pageMeta ?? null;
  } catch {
    // 청크 로드 실패는 vue-router 가 다시 처리하게 둔다 (캐시하지 않음)
    return null;
  }

  _pageMetaCache.set(record, meta);
  return meta;
}

/**
 * 페이지의 definePageMeta 를 라우터 가드로 실행한다.
 * Nuxt 와 동일하게 글로벌 미들웨어 **뒤에** 등록해야 한다.
 */
export function createPageMetaGuard() {
  return async (to, from) => {
    for (const record of to.matched) {
      const meta = await resolvePageMeta(record);
      if (!meta) continue;

      const { middleware, ...rest } = meta;
      Object.assign(to.meta, rest);
      if (!middleware) continue;

      for (const mw of Array.isArray(middleware) ? middleware : [middleware]) {
        if (typeof mw !== "function") continue;

        let result;
        _inMiddleware = true;
        try {
          result = await mw(to, from);
        } finally {
          _inMiddleware = false;
        }

        if (result === false) return false;
        if (typeof result === "string") return result;
        if (result && typeof result === "object") return result;
      }
    }
  };
}

/* ------------------------------------------------------------------ */
/* definePageMeta — 폴백                                               */
/* ------------------------------------------------------------------ */

// 매크로가 끌어올리지 못한 경우(일반 <script> 블록이 있는 페이지)에만 쓰인다.
// 정상 경로에서는 빌드 시 호출 자체가 제거되므로 실행되지 않는다.
export function definePageMeta(meta = {}) {
  if (!_router) return;

  const route = _router.currentRoute.value;
  const { middleware, ...rest } = meta;

  Object.assign(route.meta, rest);
  if (getCurrentInstance()) {
    Object.assign(getCurrentInstance().type.__pageMeta || (getCurrentInstance().type.__pageMeta = {}), rest);
  }

  if (!middleware) return;

  const list = Array.isArray(middleware) ? middleware : [middleware];
  for (const mw of list) {
    if (typeof mw !== "function") continue;

    _inMiddleware = true;
    let result;
    try {
      result = mw(route, route);
    } finally {
      _inMiddleware = false;
    }

    if (result && typeof result === "object") {
      const { replace, ...loc } = result;
      replace ? _router.replace(loc) : _router.push(loc);
      break;
    }
    if (typeof result === "string") {
      _router.push(result);
      break;
    }
    if (result === false) break;
  }
}

/* ------------------------------------------------------------------ */
/* plugin / 기타                                                        */
/* ------------------------------------------------------------------ */

export const defineNuxtPlugin = (fn) => (typeof fn === "function" ? fn : fn.setup);

// SSR 전용 API — SPA에서는 no-op
export const useRequestHeaders = () => ({});
export const useRequestURL = () => new URL(window.location.href);
export const onNuxtReady = (cb) => {
  if (document.readyState === "complete") setTimeout(cb, 0);
  else window.addEventListener("load", () => setTimeout(cb, 0));
};
